/**
 * Notification Context for global notification management
 *
 * @author Claude
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Alert, Snackbar, AlertColor } from '@mui/material';

/**
 * Notification options interface
 */
export interface NotificationOptions {
  /**
   * Notification message
   */
  message: string;

  /**
   * Notification severity
   */
  severity?: AlertColor;

  /**
   * Auto-hide duration in milliseconds
   */
  duration?: number;

  /**
   * Action button configuration
   */
  action?: {
    label: string;
    onClick: () => void;
  };

  /**
   * Notification ID for deduplication
   */
  id?: string;

  /**
   * Whether to persist notification across navigation
   */
  persist?: boolean;
}

/**
 * Notification interface
 */
interface Notification extends NotificationOptions {
  id: string;
  timestamp: number;
}

/**
 * Notification context interface
 */
interface NotificationContextValue {
  /**
   * Show a notification
   */
  showNotification: (options: NotificationOptions) => void;

  /**
   * Show success notification
   */
  showSuccess: (message: string, options?: Partial<NotificationOptions>) => void;

  /**
   * Show error notification
   */
  showError: (message: string, options?: Partial<NotificationOptions>) => void;

  /**
   * Show warning notification
   */
  showWarning: (message: string, options?: Partial<NotificationOptions>) => void;

  /**
   * Show info notification
   */
  showInfo: (message: string, options?: Partial<NotificationOptions>) => void;

  /**
   * Clear all notifications
   */
  clearNotifications: () => void;

  /**
   * Current active notification
   */
  currentNotification: Notification | null;

  /**
   * Whether in-app notifications are enabled (user preference)
   */
  notificationsEnabled: boolean;

  /**
   * Update in-app notification preference
   */
  setNotificationsEnabled: (enabled: boolean) => void;
}

// Create context
const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

/**
 * Notification Provider component
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const [notificationsEnabled, setNotificationsEnabledState] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('notificationsEnabled');
      // Treat null/undefined (key not yet written) as enabled; only 'false' disables
      if (saved == null) return true;
      return saved !== 'false';
    } catch {
      return true;
    }
  });

  // Persist notification preference whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('notificationsEnabled', String(notificationsEnabled));
    } catch {
      // Ignore localStorage errors
    }
  }, [notificationsEnabled]);

  const setNotificationsEnabled = useCallback((enabled: boolean) => {
    setNotificationsEnabledState(enabled);
    if (!enabled) {
      // Clear any currently displayed notifications when the user disables them
      setNotifications([]);
      setCurrentNotification(null);
    }
  }, []);

  // Generate unique ID
  const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Show notification
  const showNotification = useCallback(
    (options: NotificationOptions) => {
      // Silently discard if the user has disabled in-app notifications
      if (!notificationsEnabled) {
        return;
      }

      const notification: Notification = {
        ...options,
        id: options.id || generateId(),
        timestamp: Date.now(),
        severity: options.severity || 'info',
        duration: options.duration || 6000,
      };

      // Check for duplicate ID
      setNotifications(prev => {
        const filtered = prev.filter(n => n.id !== notification.id);
        return [...filtered, notification];
      });

      // Set as current notification
      setCurrentNotification(notification);
    },
    [notificationsEnabled]
  );

  // Convenience methods
  const showSuccess = useCallback(
    (message: string, options?: Partial<NotificationOptions>) => {
      showNotification({ ...options, message, severity: 'success' });
    },
    [showNotification]
  );

  const showError = useCallback(
    (message: string, options?: Partial<NotificationOptions>) => {
      showNotification({
        ...options,
        message,
        severity: 'error',
        duration: options?.duration || 8000,
      });
    },
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string, options?: Partial<NotificationOptions>) => {
      showNotification({ ...options, message, severity: 'warning' });
    },
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string, options?: Partial<NotificationOptions>) => {
      showNotification({ ...options, message, severity: 'info' });
    },
    [showNotification]
  );

  // Clear all notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setCurrentNotification(null);
  }, []);

  // Handle close
  const handleClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }

      if (currentNotification) {
        setNotifications(prev => prev.filter(n => n.id !== currentNotification.id));

        // Show next notification if any
        const remaining = notifications.filter(n => n.id !== currentNotification.id);
        if (remaining.length > 0) {
          setCurrentNotification(remaining[0]);
        } else {
          setCurrentNotification(null);
        }
      }
    },
    [currentNotification, notifications]
  );

  const value: NotificationContextValue = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearNotifications,
    currentNotification,
    notificationsEnabled,
    setNotificationsEnabled,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      <Snackbar
        open={!!currentNotification}
        autoHideDuration={currentNotification?.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 10 }}
      >
        {currentNotification ? (
          <Alert
            onClose={handleClose}
            severity={currentNotification.severity}
            variant="filled"
            sx={{ width: '100%', minWidth: 300 }}
            action={
              currentNotification.action ? (
                <button
                  onClick={() => {
                    currentNotification.action?.onClick();
                    handleClose();
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: 'inherit',
                    padding: '6px 8px',
                  }}
                >
                  {currentNotification.action.label}
                </button>
              ) : undefined
            }
          >
            {currentNotification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
};

/**
 * Hook to use notification context
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
